//#region IMPORTS
import {
  InstoreTemplate,
  InstoreTemplateRow,
  InstoreTemplateRequest,
  InstoreTemplateTableRow,
  InstoreTemplateRowRequest,
} from "../models/instoreTemplate";
import {
  updateInstoreTemplateAsync,
  instoreTemplateRowSelectors,
} from "../features/instoreTemplates/instoreTemplatesSlice";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../store/configureStore";
import { toBackendDate } from "../helpers/toBackendDate";
import { resolveClearableField } from "../helpers/resolveClearableField";
import { InstoreTheme } from "../models/instoreTheme";
//#endregion

export function useLocalTemplateRows(template: InstoreTemplate) {
  //#region REDUX STATE
  const dispatch = useAppDispatch();
  const reduxRows = useAppSelector(instoreTemplateRowSelectors.selectAll);
  const rowsLoaded = useAppSelector(
    (state) => state.instoreTemplates.instoreTemplateRowsLoaded,
  );
  //#endregion

  //#region LOCAL STATE
  const [localTableData, setLocalTableData] = useState<
    InstoreTemplateTableRow[]
  >([]);
  const [isDirty, setIsDirty] = useState(false);
  //#endregion

  //#region HELPERS
  const recalcFlatIndexes = useCallback((rows: InstoreTemplateTableRow[]) => {
    let flatIndex = 0;
    const assign = (row?: InstoreTemplateTableRow) => {
      if (!row) return;
      row.flatIndex = flatIndex++;
      row.children?.forEach(assign);
    };
    rows.forEach(assign);
  }, []);
  //#endregion

  //#region DISTINCT STORE FORMATS
  const distinctStoreFormats = useMemo(() => {
    if (!rowsLoaded || !reduxRows || reduxRows.length === 0) return [];
    const map = new Map<string, { id: number; name: string }>();
    reduxRows.forEach((item) => {
      const id = item.store_format_id ?? 0;
      const name = item.store_format ?? "";
      const key = String(id !== 0 ? id : name);
      if (!map.has(key)) map.set(key, { id, name });
    });
    return Array.from(map.values());
  }, [reduxRows, rowsLoaded]);
  //#endregion

  //#region TABLE DATA BUILDER
  const buildTableData = useCallback(
    (rows: InstoreTemplateRow[]): InstoreTemplateTableRow[] => {
      if (!rows) return [];

      const rowsToRender: InstoreTemplateTableRow[] = [];
      const themes = new Map<string | number, InstoreTemplateRow[]>();

      for (const item of rows) {
        const key = item.theme_id ?? item.theme ?? `theme_${String(item.id)}`;
        if (!themes.has(key)) themes.set(key, []);
        themes.get(key)!.push(item);
      }

      for (const items of themes.values()) {
        if (!items.length) continue;
        const themeInfo = items[0];

        const articles = new Map<
          string,
          { items: InstoreTemplateRow[]; isDuplicate: boolean }
        >();
        const seenFormatKeys = new Set<string>();

        for (const it of items) {
          const baseName = (it.article_name ?? "").trim();
          const formatKey = `${baseName}_${it.store_format_id ?? ""}`;

          let aKey: string;
          let isDuplicate = false;

          if (seenFormatKeys.has(formatKey)) {
            // duplicate article_name + store_format_id → own isolated row
            aKey = `duplicate_${String(it.id)}`;
            isDuplicate = true;
          } else {
            seenFormatKeys.add(formatKey);
            aKey = baseName || `article_${String(it.id)}`;
          }

          if (!articles.has(aKey)) {
            articles.set(aKey, { items: [], isDuplicate });
          }

          articles.get(aKey)!.items.push(it);
        }

        const children: InstoreTemplateTableRow[] = [];
        for (const articleGroup of articles.values()) {
          const articleItems = articleGroup.items;
          const first = articleItems[0];

          const store_formats_amounts = articleItems.map((ai) => ({
            db_id: ai.id,
            store_format: ai.store_format,
            store_format_id: ai.store_format_id,
            amount: ai.amount,
          }));

          children.push({
            rowKind: "article",
            db_ids: articleItems.map((ai) => ai.id),
            index: first.index,
            theme: first.theme,
            theme_id: first.theme_id,
            custom_fields: first.custom_fields,
            article_name: first.article_name,
            // set/price are optional: keep null as null so an empty value is
            // not coerced to 0 (which would then be saved back to the backend).
            set: first.set ?? null,
            price: first.price ?? null,
            // remember what was loaded, to distinguish "cleared a value"
            // (→ send null) from "never had one" (→ omit) on save.
            originalSet: first.set ?? null,
            originalPrice: first.price ?? null,
            parent: false,
            isDuplicate: articleGroup.isDuplicate,
            store_formats_amounts,
          });
        }

        rowsToRender.push({
          rowKind: "theme",
          index: 0,
          db_ids: items.map((i) => i.id),
          theme: themeInfo.theme,
          theme_id: themeInfo.theme_id,
          article_name: themeInfo.theme,
          parent: true,
          isDuplicate: false,
          store_formats_amounts: [],
          children: children.sort((a, b) => (a.index ?? 0) - (b.index ?? 0)),
        });
      }

      recalcFlatIndexes(rowsToRender);
      return rowsToRender;
    },
    [recalcFlatIndexes],
  );
  //#endregion

  //#region ADD THEME ROW
  const addThemeRow = useCallback(
    (theme: InstoreTheme) => {
      setLocalTableData((prev) => {
        const next = [...prev];
        const parent: InstoreTemplateTableRow = {
          rowKind: "theme",
          parent: true,
          db_ids: [],
          theme: theme.name,
          theme_id: theme.id,
          article_name: theme.name,
          index: 0,
          store_formats_amounts: [],
          children: [],
        };
        const child: InstoreTemplateTableRow = {
          rowKind: "article",
          parent: false,
          db_ids: [],
          theme: theme.name,
          theme_id: theme.id,
          article_name: "enter name",
          set: null,
          price: null,
          originalSet: null,
          originalPrice: null,
          index: 0,
          custom_fields: [],
          placeholder: true,
          store_formats_amounts: distinctStoreFormats.map((sf) => ({
            db_id: undefined,
            store_format: sf.name,
            store_format_id: sf.id,
            amount: 0,
          })),
        };
        parent.children!.push(child);
        next.push(parent);

        recalcFlatIndexes(next);
        setIsDirty(true);
        return next;
      });
    },
    [distinctStoreFormats, recalcFlatIndexes],
  );
  //#endregion

  //#region ADD ARTICLE ROW
  const addArticleRow = useCallback(
    (themeId: number) => {
      setLocalTableData((prev) => {
        const next = [...prev];
        const parent = next.find((r) => r.theme_id === themeId && r.parent);
        if (!parent) return prev;

        const newArticle: InstoreTemplateTableRow = {
          rowKind: "article",
          parent: false,
          db_ids: [],
          theme: parent.theme,
          theme_id: parent.theme_id,
          article_name: "enter a name",
          set: null,
          price: null,
          originalSet: null,
          originalPrice: null,
          index: 0,
          custom_fields: [],
          placeholder: true,
          store_formats_amounts: distinctStoreFormats.map((sf) => ({
            db_id: undefined,
            store_format: sf.name,
            store_format_id: sf.id,
            amount: 0,
          })),
        };

        parent.children = parent.children
          ? [...parent.children, newArticle]
          : [newArticle];

        recalcFlatIndexes(next);
        setIsDirty(true);
        return next;
      });
    },
    [distinctStoreFormats, recalcFlatIndexes],
  );
  //#endregion

  //#region MOVE ROW ANYWHERE
  const moveRowAnywhere = useCallback(
    (fromFlatIndex: number, toFlatIndex: number) => {
      setLocalTableData((prev) => {
        const allRows: InstoreTemplateTableRow[] = [];
        prev.forEach((parent) => {
          allRows.push(parent);
          parent.children?.forEach((child) => allRows.push(child));
        });

        if (
          fromFlatIndex < 0 ||
          fromFlatIndex >= allRows.length ||
          toFlatIndex < 0 ||
          toFlatIndex >= allRows.length
        ) {
          console.warn("Invalid flatIndex for moveRowAnywhere", {
            fromFlatIndex,
            toFlatIndex,
            totalRows: allRows.length,
          });
          return prev;
        }

        const movingRow = allRows[fromFlatIndex];
        const movedRows = movingRow.parent
          ? [movingRow, ...(movingRow.children ?? [])]
          : [movingRow];

        allRows.splice(fromFlatIndex, movedRows.length);
        allRows.splice(toFlatIndex, 0, ...movedRows);

        const newParents: InstoreTemplateTableRow[] = [];
        let currentParent: InstoreTemplateTableRow | null = null;

        for (const row of allRows) {
          if (row.parent) {
            currentParent = { ...row, children: [] };
            newParents.push(currentParent);
          } else {
            if (!currentParent) continue;
            row.theme = currentParent.theme;
            row.theme_id = currentParent.theme_id;
            currentParent.children!.push(row);
          }
        }

        const cleanedParents = newParents.filter(
          (p) => !p.parent || (p.children && p.children.length > 0),
        );

        recalcFlatIndexes(cleanedParents);
        setIsDirty(true);
        return cleanedParents;
      });
    },
    [recalcFlatIndexes],
  );
  //#endregion

  //#region UPDATE ROW CELLS LOCALLY
  const updateRowCells = useCallback(
    (columnId: string, value: any, row: InstoreTemplateTableRow) => {
      setLocalTableData((prev) => {
        let updated = false;

        const recursiveUpdate = (rows: InstoreTemplateTableRow[]) =>
          rows.map((r) => {
            if (r === row) {
              updated = true;

              if (columnId.startsWith("store_format_")) {
                const [, , storeFormatIdStr, storeFormatName] =
                  columnId.split("_");
                const storeFormatId = Number(storeFormatIdStr);

                const existing = r.store_formats_amounts.find(
                  (sfa) => sfa.store_format_id === storeFormatId,
                );

                if (existing) {
                  if (Number(value) === 0) {
                    r.store_formats_amounts = r.store_formats_amounts.filter(
                      (sfa) => sfa.store_format_id !== storeFormatId,
                    );
                  } else {
                    existing.amount = Number(value);
                  }
                } else if (value !== 0) {
                  r.store_formats_amounts.push({
                    db_id: undefined,
                    store_format_id: storeFormatId,
                    store_format: storeFormatName,
                    amount: Number(value),
                  });
                }
              } else if (columnId === "article_name") {
                r.article_name = value;
                r.placeholder = false;
              } else if (columnId === "set") {
                // empty input clears the field (null) instead of becoming 0
                r.set =
                  value === "" || value === null || value === undefined
                    ? null
                    : Number(value);
              } else if (columnId === "price") {
                r.price =
                  value === "" || value === null || value === undefined
                    ? null
                    : Number(value);
              } else if (columnId === "custom_fields") {
                r.custom_fields = value[0] === "" ? null : value;
              }

              return r;
            }

            if (r.children) r.children = recursiveUpdate(r.children);
            return r;
          });

        const newData = recursiveUpdate(prev);
        if (updated) setIsDirty(true);
        return newData;
      });
    },
    [],
  );
  //#endregion

  //#region DELETE ROW LOCALLY
  const removeRow = useCallback(
    (row: InstoreTemplateTableRow) => {
      setLocalTableData((prev) => {
        const filterRows = (
          rows: InstoreTemplateTableRow[],
        ): InstoreTemplateTableRow[] =>
          rows
            .filter((r) => r !== row)
            .map((r) => {
              if (r.children) r.children = filterRows(r.children);
              return r;
            })
            .filter((r) =>
              r.parent ? r.children && r.children.length > 0 : true,
            ); // remove parent with no children

        const newData = filterRows(prev);
        recalcFlatIndexes(newData);
        setIsDirty(true);
        return newData;
      });
    },
    [recalcFlatIndexes],
  );

  //#endregion

  //#region FLATTEN FOR BACKEND
  const flattenForBackend = useCallback(
    (localRows: InstoreTemplateTableRow[]): InstoreTemplateRowRequest[] => {
      const out: InstoreTemplateRowRequest[] = [];
      for (const parent of localRows) {
        parent.children?.forEach((child) => {
          // set/price are per-article, so resolve the outbound value once and
          // apply it to every store-format row of this article.
          const set = resolveClearableField(child.set, child.originalSet);
          const price = resolveClearableField(child.price, child.originalPrice);
          child.store_formats_amounts.forEach((sfa) => {
            out.push({
              id: sfa.db_id,
              theme_id: parent.theme_id,
              store_format_id: sfa.store_format_id,
              custom_fields: child.custom_fields || [],
              article_name: child.article_name || "",
              amount: sfa.amount,
              set,
              price,
              index: child.flatIndex,
            });
          });
        });
      }
      return out;
    },
    [],
  );
  //#endregion

  //#region SAVE TO BACKEND
  const saveChanges = useCallback(async () => {
    if (!template) return;
    const mappedRows = flattenForBackend(localTableData);

    const request: InstoreTemplateRequest = {
      custom_template_id: template.custom_template_id || 0,
      instore_type_id: template.instore_type_id,
      order_type_instore_id: template.order_type_instore_id,
      active: template.active,
      from: toBackendDate(template.from),
      until: toBackendDate(template.until),
      template_rows: mappedRows.map((row) => ({
        id: row.id && row.id > 0 ? row.id : undefined,
        theme_id: row.theme_id,
        store_format_id: row.store_format_id,
        custom_fields: row.custom_fields,
        article_name: row.article_name,
        amount: row.amount,
        set: row.set,
        price: row.price,
        index: row.index,
      })),
    };

    await dispatch(
      updateInstoreTemplateAsync({
        id: template.id,
        values: request,
      }),
    );

    setIsDirty(false);
  }, [template, localTableData, dispatch, flattenForBackend]);
  //#endregion

  //#region INITIALIZE LOCAL STATE
  useEffect(() => {
    if (!rowsLoaded || !reduxRows || reduxRows.length === 0) return;
    setLocalTableData(
      buildTableData(reduxRows.sort((a, b) => (a.index ?? 0) - (b.index ?? 0))),
    );
  }, [rowsLoaded, reduxRows, buildTableData]);
  //#endregion

  //#region RETURNED API
  return {
    distinctStoreFormats,
    setLocalTableData,
    moveRowAnywhere,
    localTableData,
    updateRowCells,
    addArticleRow,
    saveChanges,
    addThemeRow,
    removeRow,
    isDirty,
  };
  //#endregion
}
